import Error from '@components/elements/error/error';
import FileInput, {
  fieldToFileInputProps,
} from '@components/elements/file-input/file-input';
import InputArea, {
  fieldToInputAreaProps,
} from '@components/elements/input-area/input-area';
import InputEnvelope from '@components/elements/input-envelope/input-envelope';
import Input, { fieldToInputProps } from '@components/elements/input/input';
import Select, { fieldToSelectProps } from '@components/elements/select/select';
import Success from '@components/elements/success/success';
import Switch, { fieldToSwitchProps } from '@components/elements/switch/switch';
import {
  v2_useAuthorizedCustomer,
  v2_useBranches,
  v2_useContact,
} from '@crocodille/common';
import * as routing from '@routing';
import { translateError } from '@utils/error';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Button from '../../components/elements/button/button';
import { parse, format } from 'date-fns';

import styles from './contact-view.module.scss';
import SpinnerIcon from '@components/icons/spinner-icon';

const ContactView: React.FC = () => {
  const { t } = useTranslation();
  const { query } = useRouter();

  const { data: customer } = v2_useAuthorizedCustomer();
  const {
    form: { fields, handleSubmit, reset },
    isMutating,
    error,
    success,
  } = v2_useContact();

  const { data: branches } = v2_useBranches();

  const preSetBranch = branches?.find((b) => b.code === query.branchCode);

  const formatDate = (dateStr: string | string[] | undefined) => {
    if (typeof dateStr !== 'string') {
      return;
    }

    const parsedDate = parse(dateStr, 'dd.MM.yyyy', new Date());
    return format(parsedDate, 'yyyy-MM-dd');
  };

  useEffect(() => {
    reset({
      email: customer?.email ?? '',
      receiptCode: query.orderNumber as string | undefined,
      branch: preSetBranch?.name,
      dateOfVisit: formatDate(query.date),
      timeOfVisit: query.time as string | undefined,
    });
  }, [
    customer?.email,
    preSetBranch?.name,
    query.date,
    query.orderNumber,
    query.time,
    reset,
  ]);

  return (
    <>
      <div className={styles.container}>
        <form
          className={styles.form}
          encType="multipart/form-data"
          onSubmit={(e) => {
            e.preventDefault();
            const valid = handleSubmit();

            if (fields.attachment.error) {
              fields.attachment.onChange(null);
            }

            if (valid) {
              reset({
                email: customer?.email ?? '',
                receiptCode: query.orderNumber as string | undefined,
                branch: preSetBranch?.name,
                dateOfVisit: formatDate(query.date),
                timeOfVisit: query.time as string | undefined,
              });
            }
          }}
        >
          <h1 className={styles.heading}>{t('contact.heading')}</h1>

          {success ? (
            <Success className={styles.success}>{t('contact.success')}</Success>
          ) : null}
          <div className={styles.column}>
            <InputEnvelope
              label={t('contact.receipt_code')}
              required={fields.receiptCode.required}
              error={translateError(t, fields.receiptCode.error)}
            >
              <Input
                name="receiptCode"
                type="text"
                {...fieldToInputProps(fields.receiptCode)}
              />
            </InputEnvelope>
            <InputEnvelope
              label={t('contact.message_type')}
              required={fields.messageType.required}
              error={translateError(t, fields.messageType.error)}
              noLabelElement
            >
              <Select
                name="messageType"
                {...fieldToSelectProps(fields.messageType)}
              >
                <Select.Item
                  key={''}
                  value={''}
                  label={t('contact.pick_message_type')}
                />
                <Select.Item
                  value={t('contact.message_type_complaint')}
                  label={t('contact.message_type_complaint')}
                />
                <Select.Item
                  value={t('contact.message_type_praise')}
                  label={t('contact.message_type_praise')}
                />
                <Select.Item
                  value={t('contact.message_type_question')}
                  label={t('contact.message_type_question')}
                />
                <Select.Item
                  value={t('contact.message_type_suggestion')}
                  label={t('contact.message_type_suggestion')}
                />
              </Select>
            </InputEnvelope>
            <InputEnvelope
              label={t('contact.branch')}
              required={fields.branch.required}
              error={translateError(t, fields.branch.error)}
              noLabelElement
            >
              <Select name="branch" {...fieldToSelectProps(fields.branch)}>
                <Select.Item
                  key={''}
                  value={''}
                  label={t('contact.pick_restaurant')}
                />
                {branches?.map?.(({ name, code }) => (
                  <Select.Item key={code} value={name} label={name} />
                ))}
              </Select>
            </InputEnvelope>
            <InputEnvelope
              label={t('contact.date_of_visit')}
              required={fields.dateOfVisit.required}
              error={translateError(t, fields.dateOfVisit.error)}
            >
              <Input
                name="dateOfVisit"
                type="date"
                {...fieldToInputProps(fields.dateOfVisit)}
              />
            </InputEnvelope>
            <InputEnvelope
              label={t('contact.time_of_visit')}
              required={fields.timeOfVisit.required}
              error={translateError(t, fields.timeOfVisit.error)}
            >
              <Input
                name="timeOfVisit"
                type="time"
                {...fieldToInputProps(fields.timeOfVisit)}
              />
            </InputEnvelope>
          </div>
          <div className={styles.column}>
            <InputEnvelope
              label={t('contact.email')}
              required={fields.email.required}
              error={translateError(t, fields.email.error)}
            >
              <Input
                name="email"
                type="email"
                autoComplete="email"
                {...fieldToInputProps(fields.email)}
              />
            </InputEnvelope>
            <InputEnvelope
              label={t('contact.message')}
              required={fields.message.required}
              error={translateError(t, fields.message.error, {
                maxLength: 1000,
              })}
            >
              <InputArea
                name="message"
                maxLength={1000}
                {...fieldToInputAreaProps(fields.message)}
              />
            </InputEnvelope>
            <InputEnvelope
              label={t('contact.attachment')}
              required={fields.attachment.required}
              error={translateError(t, fields.attachment.error, {
                maxSize: 15,
              })}
            >
              <FileInput
                name="attachment"
                clearFile={Boolean(fields.attachment.error)}
                {...fieldToFileInputProps(fields.attachment)}
              />
            </InputEnvelope>
            <InputEnvelope
              label={
                <Trans t={t} i18nKey={'contact.data_agreement'}>
                  <Link
                    prefetch={false}
                    href={routing.cmsPages({ page: 'terms-and-conditions' })}
                  ></Link>
                </Trans>
              }
              required={fields.agreement.required}
              error={translateError(t, fields.agreement.error)}
              innerClassName={styles.label}
            >
              <Switch
                name="agreement"
                {...fieldToSwitchProps(fields.agreement)}
              />
            </InputEnvelope>
          </div>
          <div className={styles.controls}>
            {error ? (
              <Error className={styles.error}>{t('contact.limit_error')}</Error>
            ) : null}
            <Button size="large" type="submit">
              {t('contact.submit')}
              <Button.Icon>{isMutating ? <SpinnerIcon /> : null}</Button.Icon>
            </Button>
          </div>
        </form>
        <div className={styles.callCenterContact}>
          <Trans t={t} i18nKey="tracking.call_center_contact">
            <a href="tel:+420 800 224 389" className={styles.phone}></a>
          </Trans>
        </div>
      </div>
    </>
  );
};

export default ContactView;
